import '../../home-style.scss'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import usePrGraphFilterStyles from './styles'

const PrGraphFilter = ({
  openTable,
  setOpenTable,
  totalEnergygraphDuration,
  graphTotalType,
  technicalToDate,
  technicalFromDate,
  handleChooseDate,
  mapTotalenergySeries,
  handleOpen,
  setTechnicalFromDate,
  setTechnicalToDate,
  handleTotalenergyGraph,
  handleGraphupdateOptions,
}) => {
  const classes = usePrGraphFilterStyles()

  // Remove datepickerDisable state - date pickers are always enabled
  const exportToCSV = (rows) => {
    const headings = ['Date', ...rows.map((item) => item.name)]
    const csvContent = [headings.join(',')]

    const allDates = new Set()
    rows.forEach((item) =>
      item.data.forEach((row) =>
        allDates.add(dayjs(row.x).format('YYYY/MM/DD'))
      )
    )

    allDates.forEach((date) => {
      const rowData = [date]
      rows.forEach((item) => {
        const dataForDate = item.data.find(
          (row) => dayjs(row.x).format('YYYY/MM/DD') === date
        )
        rowData.push(dataForDate ? dataForDate.y : 0)
      })
      csvContent.push(rowData.join(','))
    })

    const blob = new Blob([csvContent.join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    saveAs(blob, 'PR.csv')
  }

  const handleselectButton = (ButtonType) => {
    switch (ButtonType) {
      case 'year':
        const previous1yData = dayjs(dayjs(new Date())).subtract(11, 'month')
        setTechnicalFromDate(previous1yData)
        setTechnicalToDate(dayjs(new Date()))
        handleTotalenergyGraph(graphTotalType, 'year', previous1yData)
        break
      case '1m':
        const previous1Data = dayjs(new Date()).subtract(29, 'day')
        setTechnicalFromDate(previous1Data)
        setTechnicalToDate(dayjs(new Date()))
        handleTotalenergyGraph(graphTotalType, '1m', previous1Data)
        break
      case '3m':
        const previous3Data = dayjs(dayjs(new Date())).subtract(2, 'month')
        setTechnicalFromDate(previous3Data)
        setTechnicalToDate(dayjs(new Date()))
        handleTotalenergyGraph(graphTotalType, '3m', previous3Data)
        break
      case '6m':
        const previous6Data = dayjs(dayjs(new Date())).subtract(5, 'month')
        setTechnicalFromDate(previous6Data)
        setTechnicalToDate(dayjs(new Date()))
        handleTotalenergyGraph(graphTotalType, '6m', previous6Data)
        break
      case 'mtd':
        const firstDateOfMonth = dayjs(new Date()).startOf('month')
        setTechnicalFromDate(firstDateOfMonth)
        setTechnicalToDate(dayjs(new Date()))
        handleTotalenergyGraph(graphTotalType, 'mtd', firstDateOfMonth)
        break
      case 'ytd':
        const firstDateOfYear = dayjs(new Date()).startOf('year')
        setTechnicalFromDate(firstDateOfYear)
        setTechnicalToDate(dayjs(new Date()))
        handleTotalenergyGraph(graphTotalType, 'ytd', firstDateOfYear)
        break
      case 'day':
        const dayData = dayjs(technicalFromDate).add(0, 'month')
        setTechnicalToDate(dayData)
        handleTotalenergyGraph(graphTotalType, 'day', technicalFromDate)
        break
      case 'custom':
        handleTotalenergyGraph(graphTotalType, 'custom', technicalFromDate)
        break
      default:
        break
    }
  }

  // Modified to handle date picker changes from any tab
  const handleDatePickerChange = (value, type) => {
    // Always update the date state
    handleChooseDate(value, type)
    
    // If current tab is not CUST, automatically switch to custom mode 
    // and fetch data with custom parameters
    if (totalEnergygraphDuration !== 'custom') {
      // Update the date state first
      if (type === 'from') {
        setTechnicalFromDate(value)
      } else {
        setTechnicalToDate(value)
      }
      
      // Then trigger custom data fetch with the updated dates
      setTimeout(() => {
        handleTotalenergyGraph(graphTotalType, 'custom', type === 'from' ? value : technicalFromDate)
      }, 0)
    }
  }

  const handleTotalenergygraphSort = (type) => {
    // Remove setDatepickerDisable(true) - date pickers stay enabled
    let tickAmount, rotateFormat, tooltipFormat, buttonType

    switch (type) {
      case 'year':
        tickAmount = 12
        rotateFormat = 'MMM/YY'
        tooltipFormat = 'MMMM/YYYY'
        buttonType = 'year'
        break
      case '1m':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = '1m'
        break
      case '3m':
      case '6m':
        tickAmount = type === '3m' ? 3 : 6
        rotateFormat = 'MMM/YY'
        tooltipFormat = 'MMMM/YYYY'
        buttonType = type
        break
      case 'ytd':
        tickAmount = 15
        rotateFormat = 'MMM/YY'
        tooltipFormat = 'MMMM/YYYY'
        buttonType = type
        break
      case 'mtd':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = type
        break
      case 'day':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = 'day'
        setTechnicalFromDate(dayjs(new Date()))
        setTechnicalToDate(dayjs(new Date()))
        break
      case 'custom':
        tickAmount = 15
        rotateFormat = 'DD/MM'
        tooltipFormat = 'DD/MMMM/YYYY'
        buttonType = 'custom'
        // Remove setDatepickerDisable(false) - already always enabled
        break
      default:
        return
    }

    const newOptions = {
      xaxis: {
        tickAmount: tickAmount || 15,
        tickPlacement: 'on',
        labels: {
          rotateAlways: true,
          formatter: (value, timestamp, opts) => {
            return dayjs(value).format(rotateFormat)
          },
        },
      },
      tooltip: {
        enabled: true,
        x: {
          show: true,
          format: 'MMMM',
          formatter: (value, opts) => {
            return dayjs(
              opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].x
            ).format(tooltipFormat)
          },
        },
      },
    }

    if (!openTable) {
      handleGraphupdateOptions(newOptions)
    }

    handleselectButton(buttonType)
  }

  const datePickerStyle = {
    width: '130px',
  }

  return (
    <div className={classes.root}>
      <div className="align_btw mb_10">
        <div className="h1">Aggregated Performance Ratio</div>

        <div>
          <div className="align_end">
            <div class="check_graph mr_8">
              <input
                id="Prgraph"
                type="checkbox"
                checked={openTable}
                onChange={() => setOpenTable(!openTable)}
              />
              <label for="Prgraph"></label>
            </div>
            <div className="mr_8">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={['DatePicker']}>
                  <div style={datePickerStyle}>
                    <DatePicker
                      style={{ width: '120px !important' }}
                      label="From Date"
                      maxDate={
                        totalEnergygraphDuration === 'custom'
                          ? technicalToDate
                          : dayjs()
                      }
                      minDate={dayjs(new Date()).subtract(2, 'year')}
                      className={classes.datePicker}
                      format="DD-MM-YYYY"
                      value={technicalFromDate}
                      onChange={(value) => handleDatePickerChange(value, 'from')}
                      disabled={false} // Always enabled
                    />
                  </div>
                </DemoContainer>
              </LocalizationProvider>
            </div>
            <div className="mr_8">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={['DatePicker']}>
                  <div style={datePickerStyle}>
                    <DatePicker
                      style={{ width: '120px !important' }}
                      label="To Date"
                      maxDate={dayjs()}
                      minDate={
                        totalEnergygraphDuration === 'custom'
                          ? technicalFromDate
                          : dayjs(new Date()).subtract(2, 'year')
                      }
                      className={classes.datePicker}
                      format="DD-MM-YYYY"
                      value={technicalToDate}
                      onChange={(value) => handleDatePickerChange(value, 'to')}
                      disabled={false} // Always enabled
                    />
                  </div>
                </DemoContainer>
              </LocalizationProvider>
            </div>
            {openTable && (
              <div className="mr_8">
                <VerticalAlignBottomIcon
                  className="i_clr pointer"
                  onClick={() => exportToCSV(mapTotalenergySeries)}
                />
              </div>
            )}
            <div className="mt8">
              <FullscreenIcon className="i_clr pointer" onClick={handleOpen} />
            </div>
          </div>
        </div>
      </div>

      <div className="align_end mb_10">
        <div>
          <div
            className={
              totalEnergygraphDuration === 'custom'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('custom')}
          >
            CUST
          </div>
          <div
            className={
              totalEnergygraphDuration === 'ytd'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('ytd')}
          >
            YTD
          </div>
          <div
            className={
              totalEnergygraphDuration === 'mtd'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('mtd')}
          >
            MTD
          </div>
          <div
            className={
              totalEnergygraphDuration === 'year'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('year')}
          >
            1 Y
          </div>
          <div
            className={
              totalEnergygraphDuration === '6m'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('6m')}
          >
            6 M
          </div>
          <div
            className={
              totalEnergygraphDuration === '3m'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('3m')}
          >
            3 M
          </div>
          <div
            className={
              totalEnergygraphDuration === '1m'
                ? ' day_s_bottomline'
                : 'day_s_b '
            }
            onClick={() => handleTotalenergygraphSort('1m')}
          >
            1 M
          </div>
        </div>
      </div>
    </div>
  )
}

PrGraphFilter.propTypes = {}

export default PrGraphFilter
